import type { CartItem } from "@/features/cart/components/CartItem";
import type { ProductCard, ProductDetails } from "@/lib/centra";
import type { PreoderProduct } from "@kaos/graphql/queries";

export const isProductBundle = (
	product: ProductDetails | ProductCard | CartItem,
): boolean => {
	if ("line" in product && "quantity" in product) {
		const selectionItem = product as CartItem;
		return selectionItem?.product
			? (selectionItem.product.name?.toLowerCase().includes("bundle") ?? false) ||
					(selectionItem.product.uri?.toLowerCase().includes("bundle") ?? false)
			: false;
	}

	const regularProduct = product as ProductDetails | ProductCard;
	return (
		regularProduct.canonicalCategoryUri === "bundles" ||
		regularProduct.canonicalUri?.includes("bundles") ||
		regularProduct.name.toLowerCase().includes("bundle") ||
		regularProduct.uri.toLowerCase().includes("bundle")
	);
};

export const checkProductPreorder = ({
	centraVariantId,
	stock,
	country,
	preorderData,
	language,
}: {
	centraVariantId: number;
	stock: number;
	country: string;
	preorderData: PreoderProduct[];
	language: string;
}): { eta: string } | false => {
	if (stock > 0) {
		return false;
	}

	const preorder = preorderData.find((p) => p.variantId === centraVariantId);

	if (!preorder) {
		return false;
	}

	const warehouseMatch = preorder.warehouses.find((warehouse) =>
		warehouse.countryCodes.includes(country),
	);

	if (!warehouseMatch) {
		return false;
	}

	const eta = new Date(warehouseMatch.eta);
	const today = new Date();

	if (eta.getTime() < today.getTime()) {
		return false;
	}

	return { eta: eta.toLocaleDateString(language) };
};

/**
 * Centra's aggregate `item.stock` counts incoming deliveries, so an item with
 * nothing free-to-allocate in the market's warehouse can still report "100+"
 * and render as buyable. `marketStock` (see `useStock`) resolves the market's
 * allocation rule down to its warehouse, so an explicit 0 there is the truth.
 *
 * `undefined`/`null` mean "not loaded" or "lookup failed" — never sold out.
 * A pre-order ETA takes precedence, since that flow is the point of selling
 * stock that hasn't landed yet. Bundles are skipped: the lookup reads the
 * display's first product variant, which is meaningless for a bundle.
 */
export const isSoldOutInMarket = ({
	marketStock,
	hasPreorder,
	isBundle,
}: {
	marketStock: number | null | undefined;
	hasPreorder: boolean;
	isBundle: boolean;
}): boolean => marketStock === 0 && !hasPreorder && !isBundle;
